import { parseObj } from '@/utils';

import { isDouyinAppChannel, getAppChannelValue, EAPP_CHANNEL_VALUE } from './code';
import { canIUseUnionPaySDK } from './utils';
import { getPrivacyAgreed } from '@/pages/Privacy/config';

export interface LocationInfo {
  latitude: number;
  longitude: number;
}

const canUseLocalStorage = typeof localStorage !== undefined;

export const DEFAULT_TIME_OUT_MS = 3500;

// [unionpay] 48h
const LOCATION_UNALLOWED_EXPOIRATION = 1000 * 60 * 60 * 48;
const LOCAL_LOCATION_UNALLOWED_TIME_MS_KEY = 'UNICONPAY_LOCATION_UNALLOWED_TIME_MS';
const locationUnallowedCountdownCache = {
  block: () => {
    if (canUseLocalStorage) {
      const _start = Number(localStorage.getItem(LOCAL_LOCATION_UNALLOWED_TIME_MS_KEY));
      if (_start) {
        const _now = Date.now();
        if (_now - _start <= LOCATION_UNALLOWED_EXPOIRATION) {
          return true;
        }
      }
    }

    return false;
  },
  errCall: (code: string) => {
    // 用户未授权
    if (String(code) === '1002') {
      if (canUseLocalStorage) {
        localStorage.setItem(LOCAL_LOCATION_UNALLOWED_TIME_MS_KEY, String(Date.now()));
      }
    }
  },
};

/**
 * [unionpay] getLocationGps promise
 */
export const getUnionPaySDKLocationInfo = (): Promise<LocationInfo> => {
  return new Promise((resolve, reject) => {
    if (locationUnallowedCountdownCache.block()) {
      reject(new Error('[getUnionPaySDKLocationInfo] error. Authorization denied has not expired'));
      return;
    }

    const timer = setTimeout(() => {
      reject(new Error('[getUnionPaySDKLocationInfo] error. time out'));
    }, DEFAULT_TIME_OUT_MS);

    if (!canIUseUnionPaySDK || typeof upsdk === 'undefined') {
      clearTimeout(timer);
      reject(new Error('[getUnionPaySDKLocationInfo] error. upsdk cannot be used'));
      return;
    }

    upsdk.getLocationGps({
      success: (d: LocationInfo | string | undefined) => {
        let _loc = d;
        // TODO 区分ios与安卓
        if (typeof _loc === 'string') {
          _loc = parseObj<LocationInfo>(_loc);
        }

        timer && clearTimeout(timer);

        if (_loc && typeof _loc === 'object' && _loc.latitude != null && _loc.longitude != null) {
          const { latitude, longitude } = _loc;
          resolve({ latitude, longitude });
        } else {
          reject(new Error('[getUnionPaySDKLocationInfo] Invalid location position'));
        }
      },
      fail: (e) => {
        console.error('[getUnionPaySDKLocationInfo] fail: ', e);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        locationUnallowedCountdownCache.errCall(String((e as any)?.code));
        timer && clearTimeout(timer);
        reject(e);
      },
    });
  });
};

export const TIKTOK_LOCATION_STORAGE_KEY = 'TIKTOK_LOCATION_STORAGE_KEY';

export const setLocationForTiktok = (info?: LocationInfo) => {
  if (isDouyinAppChannel() && canUseLocalStorage) {
    localStorage.setItem(TIKTOK_LOCATION_STORAGE_KEY, JSON.stringify(info));
  }
};

export const getLocationForTiktok = () => {
  if (isDouyinAppChannel() && canUseLocalStorage) {
    return parseObj<LocationInfo>(localStorage.getItem(TIKTOK_LOCATION_STORAGE_KEY));
  }
  return undefined;
};

/**
 * [tiktok] get location cache promise
 */
export const getTikTokLocationInfo = (): Promise<LocationInfo> => {
  return new Promise((resolve, reject) => {
    const info = getLocationForTiktok();

    if (info && typeof info === 'object' && info?.latitude != null && info.longitude != null) {
      resolve(info);
    } else {
      reject(new Error('[getTikTokLocationInfo] Invalid location position'));
    }
  });
};

/**
 * get location postion info
 * 在用户同意隐私协议前，不允许调用获取经纬度API
 */
export async function channelGetLocationInfo(): Promise<LocationInfo> {
  if (!getPrivacyAgreed()) {
    throw new Error('[channelGetLocationInfo]: Privacy agreement not agreed');
  }

  const _channel = getAppChannelValue();

  let _lcoation: LocationInfo | undefined = undefined;

  if (_channel === EAPP_CHANNEL_VALUE.UNIONPAY) {
    _lcoation = await getUnionPaySDKLocationInfo();
  } else if (_channel === EAPP_CHANNEL_VALUE.DOUYIN) {
    _lcoation = await getTikTokLocationInfo();
  } else if ('geolocation' in navigator) {
    _lcoation = await new Promise<LocationInfo>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(new Error(`[channelGetLocationInfo] Geolocation error: ${error.message}`));
        },
        {
          enableHighAccuracy: true,
          timeout: DEFAULT_TIME_OUT_MS,
          maximumAge: 0,
        },
      );
    });
  }

  if (_lcoation) {
    return _lcoation;
  }
  throw new Error('[channelGetLocationInfo]: Current channel is not supported');
}
